import { Form, Input, Tag, Button, Row, Col, message } from 'antd'
import { CopyOutlined, SearchOutlined, UsergroupAddOutlined } from '@ant-design/icons'
import { useMyContext } from 'Context/MyContextProvider'
import React, { useEffect, useState } from 'react'
import { ROW_GUTTER } from 'constants/ThemeConstant'
import ResponsiveModal from 'components/shared-components/ResponsiveModal'
import VirtualList from 'rc-virtual-list'
import { PRIMARY } from '../../CustomUtils.js/utils'

const chunkArray = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
    }
    return chunks;
};

const CommonNumberField = ({ numbers, additionalNumbers = [], number, setNumber, isMobile, onShowGroups }) => {
    const { getNumberCount } = useMyContext()
    const [combinedAdditional, setCombinedAdditional] = useState('')
    const [modalVisible, setModalVisible] = useState(false)
    const [searchValue, setSearchValue] = useState('')

    const buildUniqueList = (value) => {
        if (!value) return ''
        const match = value.match(/\n*$/)
        const trailingNewlines = match ? match[0] : ''
        const mainContent = value.slice(0, value.length - trailingNewlines.length)

        const seen = new Set()
        const cleaned = mainContent
            .split('\n')
            .map((item) => item.trim())
            .filter(Boolean)
            .filter((item) => {
                if (seen.has(item)) return false
                seen.add(item)
                return true
            })
            .join('\n')

        return cleaned + trailingNewlines
    }

    const filteredNumbers = searchValue
        ? numbers.filter(num => String(num)?.includes(searchValue))
        : numbers

    const columnCount = isMobile ? 3 : 6;
    const chunkedNumbers = chunkArray(filteredNumbers, columnCount);
    const chunkedData = chunkedNumbers.map((rowItems, index) => ({
        id: index,
        items: rowItems
    }));

    const handleCopyAll = () => {
        navigator.clipboard.writeText(numbers.join('\n'))
        message.success('Numbers copied to clipboard!')
    }

    useEffect(() => {
        // Combine status/excel numbers with manual input for the Additional Mobile Numbers field
        const statusText = (additionalNumbers && additionalNumbers.length > 0) ? additionalNumbers.join('\n') : '';
        const manualText = number ? number : '';
        const combined = [statusText, manualText].filter(Boolean).join('\n');
        setCombinedAdditional(buildUniqueList(combined));
    }, [additionalNumbers, number]);

    const normalizeManualNumbers = (value) => {
        return value
            .replace(/\r/g, '')
            .replace(/\n+/g, '\n')
            .replace(/\s*,\s*/g, '\n')
            .replace(/[^0-9\n]/g, '');
    }

    return (
        <>
            {numbers?.length > 0 &&
                <Form.Item className='m-0' label="Mobile Number from Groups">
                    {isMobile ? (
                        <Row gutter={[4, 4]} align="middle" wrap={false}>
                            {numbers.slice(0, 3).map((n) => (
                                <Col xs={6} key={n}>
                                    <Tag color="magenta" style={{ width: '100%', textAlign: 'center', margin: 0 }}>{n}</Tag>
                                </Col>
                            ))}
                            {numbers.length > 3 && (
                                <Col xs={6}>
                                    <Button type="link" size="small" style={{ padding: 0, width: '100%' }} onClick={() => setModalVisible(true)}>
                                        View All ({numbers.length})
                                    </Button>
                                </Col>
                            )}
                        </Row>
                    ) : (
                        <div>
                            {numbers.slice(0, 6).map((n) => (
                                <Tag key={n} color={'magenta'}>{n}</Tag>
                            ))}
                            {numbers.length > 6 && (
                                <Button type="link" onClick={() => setModalVisible(true)}>
                                    View All ({numbers.length})
                                </Button>
                            )}
                        </div>
                    )}
                    <ResponsiveModal
                        title={
                            <Row align="middle" justify="space-between" style={{ width: '100%' }}>
                                <Col style={{ fontSize: isMobile ? 15 : 16, fontWeight: 600 }}>
                                    {isMobile ? "Group Numbers" : "Mobile Numbers from Groups"}
                                </Col>
                                <Col>
                                    <Input
                                        placeholder="Search"
                                        prefix={<SearchOutlined />}
                                        value={searchValue}
                                        onChange={(e) => setSearchValue(e.target.value)}
                                        size="small"
                                        style={{ width: isMobile ? 120 : 200 }}
                                    />
                                </Col>
                            </Row>
                        }
                        open={modalVisible}
                        onClose={() => {
                            setModalVisible(false)
                            setSearchValue('')
                        }}
                        modalProps={{
                            width: 860,
                            closable: false,
                            maskClosable: true
                        }}
                        footer={
                            <Row gutter={ROW_GUTTER} justify="space-between" align="middle" style={{ width: '100%', margin: 0 }}>
                                <Col>
                                    <span style={{ color: '#6b7280', fontSize: 13 }}>
                                        Showing: <strong style={{ color: '#1890ff' }}>{filteredNumbers.length}</strong> / {numbers.length}
                                    </span>
                                </Col>
                                <Col style={{ display: 'flex', gap: 8 }}>
                                    <Button key="copy" type="primary" size="small" icon={<CopyOutlined />} onClick={handleCopyAll}>
                                        Copy All
                                    </Button>
                                    <Button key="close" size="small" onClick={() => setModalVisible(false)}>
                                        Close
                                    </Button>
                                </Col>
                            </Row>
                        }
                    >
                        <div style={{ padding: '0 4px' }}>
                            {filteredNumbers.length > 0 ? (
                                isMobile ? (
                                    /* On mobile the Drawer body already scrolls — adding VirtualList
                                       (which needs its own fixed-height scroll container) creates a
                                       second scroll context and causes an infinite top-to-bottom loop.
                                       Plain rendering lets the drawer body handle scroll naturally. */
                                    <Row gutter={[8, 8]}>
                                        {filteredNumbers.map((num, idx) => (
                                            <Col span={24 / columnCount} key={idx}>
                                                <Tag
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(num)
                                                        message.success('Copied!')
                                                    }}
                                                    style={{ cursor: 'pointer', width: '100%', textAlign: 'center', margin: 0 }}
                                                >
                                                    {num}
                                                </Tag>
                                            </Col>
                                        ))}
                                    </Row>
                                ) : (
                                    /* On desktop the Modal has no outer scroll — VirtualList owns its
                                       fixed-height viewport and only renders visible rows. */
                                    <VirtualList
                                        data={chunkedData}
                                        height={400}
                                        itemHeight={34}
                                        itemKey="id"
                                        style={{ overflowX: 'hidden' }}
                                    >
                                        {(row) => (
                                            <Row gutter={[8, 8]} style={{ marginBottom: 8, marginRight: 0, marginLeft: 0 }}>
                                                {row.items.map((num, idx) => (
                                                    <Col span={24 / columnCount} key={idx}>
                                                        <Tag
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(num)
                                                                message.success('Copied!')
                                                            }}
                                                            style={{ cursor: 'pointer', width: '100%', textAlign: 'center', margin: 0 }}
                                                        >
                                                            {num}
                                                        </Tag>
                                                    </Col>
                                                ))}
                                            </Row>
                                        )}
                                    </VirtualList>
                                )
                            ) : (
                                <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
                                    No numbers found
                                </div>
                            )}
                        </div>
                    </ResponsiveModal>
                </Form.Item>
            }
            <style dangerouslySetInnerHTML={{
                __html: `
                .mobile-numbers-form-item {
                    width: 100%;
                }
                .mobile-numbers-form-item .ant-form-item-label {
                    width: 100% !important;
                    max-width: 100% !important;
                }
                .mobile-numbers-form-item .ant-form-item-label > label {
                    width: 100% !important;
                    display: flex !important;
                    justify-content: space-between !important;
                    align-items: center !important;
                }
                .mobile-numbers-form-item .ant-form-item-label > label::after {
                    display: none !important;
                }
            `}} />
            <Form.Item
                className='m-0 mobile-numbers-form-item'
                label={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span>{`${numbers?.length > 0 ? 'Additional ' : ''}Mobile Numbers`}</span>
                        {isMobile && onShowGroups && (
                            <Button
                                type="dashed"
                                size="small"
                                onClick={onShowGroups}
                                icon={<UsergroupAddOutlined />}
                                style={{
                                    height: 'auto',
                                    fontSize: 12,
                                    fontWeight: 500,
                                    borderColor: PRIMARY,
                                    color: PRIMARY,
                                }}
                            >
                                Select from Groups
                            </Button>
                        )}
                    </div>
                }
            >
                <Input.TextArea
                    rows={5}
                    type="text"
                    value={combinedAdditional}
                    onChange={(e) => {
                        const newValue = normalizeManualNumbers(e.target.value);
                        // Keep manual input state in `number`; status/excel numbers remain in `additionalNumbers` (passed from parent)
                        setNumber(buildUniqueList(newValue));
                    }}
                />
                <p className='float-right'>Max 10000 Numbers | Total numbers: {getNumberCount(additionalNumbers, number)}</p>
            </Form.Item>
        </>
    )
}

export default CommonNumberField
